// ============================================================
// ANSICHTSSACHE-AGENT – Knopf
// ============================================================

import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 rounded-lg text-body-sm font-medium",
    // `ease-standard` statt `ease-out`: dieselbe Kurve wie ueberall
    // sonst in der Anwendung, damit sich nichts abweichend anfuehlt.
    "transition-all duration-fast ease-standard",
    // Fokus: sichtbarer Ring, aber nur fuer die Tastatur
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary",
    // Gesperrt
    "disabled:pointer-events-none disabled:opacity-40",
    // Ein Hauch Nachgeben beim Druecken — 0.97 war zu viel und
    // liess den Knopf springen, 0.985 liest sich als Widerstand.
    "active:scale-[0.985]",
    "cursor-pointer select-none",
  ].join(" "),
  {
    variants: {
      // Kein Schlagschatten an den gefuellten Knoepfen.
      //
      // Technisch waren sie immer gleich hoch wie ihre hellen
      // Nachbarn — 36 Pixel, auf zwei Nachkommastellen gemessen.
      // Sichtbar aber nicht: der Schatten verlaengert die untere
      // Kante, waehrend Rahmenknopf und Symbolknopf scharf
      // abschliessen. In einer Reihe nebeneinander liest sich das
      // als „der rote ist groesser“, und genau so wurde es auch
      // dreimal gemeldet. Die Hervorhebung traegt die Farbe, die
      // hat es nicht noetig.
      variant: {
        default: "bg-accent text-accent-contrast hover:bg-accent-hover",
        // Auf einer Seite in `bg-secondary` hat dieser Knopf
        // dieselbe Farbe wie der Grund — seine Gestalt haengt
        // dann ganz am Ring. Der bekommt deshalb dieselbe Stufe
        // wie die Kante des Rahmenknopfes.
        secondary:
          "bg-bg-secondary text-text-primary ring-1 ring-inset ring-border-control hover:bg-bg-tertiary",
        // Die Kante traegt `border-control`, nicht `border-strong`.
        //
        // Das ist die Hoehenfrage von oben, nur die andere
        // Haelfte — und die vierte Meldung derselben Sache. Der
        // Rahmenknopf und der gefuellte Knopf sind beide 36
        // Pixel hoch: gemessen im Browser (Oberkante 119,5,
        // Unterkante 155,5, beide) und pixelweise im Screenshot
        // des Nutzers nachgezaehlt (Zeile 36 bis 107 bei
        // doppelter Aufloesung, gleicher Eckradius, gleiche
        // Schriftgrundlinie). Trotzdem liest sich der helle als
        // der kleinere, und zwar weil seine Kante fuenfmal
        // schwaecher war als die des gefuellten Nachbarn. Die
        // Begruendung in Zahlen steht am Token in tokens.css.
        //
        // Voriger Versuch war `border-strong`. Der ging in die
        // richtige Richtung und war zu klein, um den Unterschied
        // zu schliessen.
        outline:
          "border border-border-control bg-bg-primary text-text-primary hover:border-text-secondary hover:bg-bg-secondary",
        ghost: "text-text-primary hover:bg-bg-secondary",
        danger: "bg-danger text-white hover:bg-danger/90",
        link: "text-accent underline-offset-4 hover:underline active:scale-100",
      },
      size: {
        sm: "h-8 rounded-md px-3 text-body-xs",
        md: "h-9 px-3.5",
        lg: "h-11 px-6 text-body-md",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, children, disabled, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {loading && (
          <svg className="h-4 w-4 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
